import { type ReactNode, memo } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { Arrow } from 'components/collapse/style';
import { useMultiSelectCheckbox } from 'components/select/hooks/useMultiSelectCheckbox';
import { Checkbox, Row, Spinner, TextInput, Typography } from 'enable-ui';

import {
  AvatarContainer,
  DropdownMenu,
  LoadingContainer,
  OptionItem,
  OptionLabel,
  SelectContainer,
  SelectTrigger,
  SelectedItem,
  SelectedList,
} from 'components/select/styles/MultiSelectCheckbox';

import AvatarIcon from 'assets/icons/avatar.png';
import { ReactComponent as CloseIcon } from 'assets/icons/close-orange.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import { isEmpty } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

export interface OptionType {
  value: string;
  label: ReactNode;
  subLabel?: ReactNode;
  src?: string;
  hasAvatar?: boolean;
}

interface MultiSelectCheckboxProps {
  options: OptionType[];
  placeholder?: ReactNode;
  selectedValues?: string[];
  onChange: (selected: OptionType[]) => void;
  onMenuScrollToBottom?: () => void;
  fetchMoreData?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  searchTerm: string;
  onSearch: (searchTerm: string) => void;
  inputPlaceholder?: string;
  localSearch?: boolean;
}

const MultiSelectCheckbox = ({
  options,
  placeholder = 'Select options',
  selectedValues = [],
  onChange,
  fetchMoreData,
  hasMore = false,
  isLoading = false,
  searchTerm = '',
  onSearch,
  inputPlaceholder = 'Search',
  localSearch = false,
}: MultiSelectCheckboxProps) => {
  const {
    handleOptionClick,
    handleRemove,
    isOpen,
    selectedOptions,
    toggleDropdown,
    localSearchTerm,
    filteredOptions,
    handleInputChange,
    dropdownPosition,
    dropdownRef,
    triggerRef,
  } = useMultiSelectCheckbox(options, selectedValues, onChange, localSearch, onSearch);

  const displayOptions = localSearch ? filteredOptions : options;

  return (
    <SelectContainer ref={dropdownRef}>
      <SelectTrigger onClick={toggleDropdown} ref={triggerRef}>
        <Arrow isOpen={false} />
        <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
          {placeholder}
        </Typography>
      </SelectTrigger>

      <DropdownMenu isOpen={isOpen} position={dropdownPosition} data-dropdown="true">
        <TextInput
          prefix={<SearchIcon />}
          placeholder={inputPlaceholder}
          value={localSearch ? localSearchTerm : searchTerm}
          onChange={handleInputChange}
        />
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchMoreData as any}
          hasMore={hasMore}
          useWindow={false}
          initialLoad={false}
          threshold={50}
        >
          {displayOptions.length > 0 ? (
            displayOptions.map((option) => (
              <OptionItem key={option.value} onClick={handleOptionClick(option)}>
                <OptionLabel>
                  <Checkbox
                    checked={selectedOptions.some((item) => item.value === option.value)}
                    onClick={handleOptionClick(option)}
                  />
                  {option.hasAvatar && (
                    <div style={{ flexShrink: 0, width: 24, height: 24 }}>
                      <img
                        src={option.src || AvatarIcon}
                        alt={option.src ? (option.label as string) : 'avatar'}
                        className="full-width full-height"
                        style={{ borderRadius: '50%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <Row direction="column">
                    <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
                      {option.label}
                    </Typography>

                    {option.subLabel && (
                      <Typography
                        className="mt-2"
                        variant="paragraph"
                        color={COLOR_CONFIG['neutral-text']}
                      >
                        {option.subLabel}
                      </Typography>
                    )}
                  </Row>
                </OptionLabel>
              </OptionItem>
            ))
          ) : (
            <Typography
              className="p-16 text-center"
              variant="body-2"
              color={COLOR.surfaceNeutralBold['neutral-11']}
            >
              No data available
            </Typography>
          )}
          {isLoading && (
            <LoadingContainer>
              <Spinner size="md" />
            </LoadingContainer>
          )}
        </InfiniteScroll>
      </DropdownMenu>

      {!isEmpty(selectedOptions) && (
        <SelectedList>
          {selectedOptions.map((option) => (
            <SelectedItem key={option.value} onClick={handleRemove(option.value)}>
              <Row align="center" className="gap-6">
                {option.hasAvatar && (
                  <div style={{ flexShrink: 0, width: 24, height: 24 }}>
                    <img
                      src={option.src || AvatarIcon}
                      alt={option.src ? (option.label as string) : 'avatar'}
                      className="full-width full-height"
                      style={{ borderRadius: '50%', objectFit: 'cover' }}
                    />
                  </div>
                )}
                <Typography
                  variant="body-3"
                  className="ellipsis"
                  height={20}
                  color={COLOR.primary['primary-6']}
                  style={{ marginLeft: !option.hasAvatar ? 8 : 0, maxWidth: 164 }}
                >
                  {option.label}
                </Typography>
              </Row>
              <CloseIcon style={{ width: 14, height: 14 }} />
            </SelectedItem>
          ))}
        </SelectedList>
      )}
    </SelectContainer>
  );
};

export default memo(MultiSelectCheckbox);
